/**
 * Arma el PDF del certificado de finalización de un curso.
 *
 * **Se genera en el navegador, con `pdf-lib`** — ADR-2026-08-25-certificado-en-pdf-lib-del-lado-del-cliente.
 * No hay endpoint ni PDF guardado: el certificado es una función de tres datos que la
 * app ya tiene (quién, qué curso, cuándo), así que se dibuja en el momento en que se
 * pide y se descarga.
 *
 * El trabajo es en dos capas:
 *
 * 1. **El arte** viene como PDF vectorial de una página, impreso desde el artboard de
 *    Claude Design, y se estampa entero con `embedPdf` — sigue siendo vectorial, así
 *    que el marco, las cenefas y el sello imprimen nítidos a cualquier tamaño.
 * 2. **Todo el texto** —el fijo y los tres datos variables— lo dibuja este módulo con
 *    las tipografías embebidas y subseteadas.
 *
 * Lo que se dibuja sale de `PLANTILLA`; este módulo no sabe nada del diseño concreto.
 */

import fontkit from '@pdf-lib/fontkit';
import { PDFDocument, rgb, type PDFFont, type RGB } from 'pdf-lib';
import {
  FUENTES,
  PLANTILLA,
  type CampoCertificado,
  type CapaTexto,
  type ClaveFuente,
} from './plantillaCertificado';

/** Lo que cambia de un certificado a otro. */
export interface DatosCertificado {
  /** Nombre de la persona, tal cual se imprime. */
  nombre: string;
  /** Título del curso, tal cual se imprime. */
  curso: string;
  /** Cuándo terminó el curso, en ms de época. */
  fecha: number;
}

/** `#RRGGBB` → el `RGB` de pdf-lib. Cae a negro ante un hex inválido. */
function color(hex: string): RGB {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return rgb(0, 0, 0);
  const n = parseInt(m[1], 16);
  return rgb(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255);
}

/**
 * "25 de agosto de 2026" — la fecha como se lee en un certificado.
 *
 * Con año y con el mes en palabras, al revés que la fecha del intento de examen: un
 * certificado se mira años después y fuera de la app, así que el año no es ruido sino
 * el dato. Sin hora, por lo mismo.
 */
export function fechaDeCertificado(ms: number): string {
  return new Date(ms).toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Ancho de un texto contando el espaciado entre letras, que pdf-lib no considera. */
function anchoDe(texto: string, fuente: PDFFont, tamano: number, espaciado: number): number {
  const base = fuente.widthOfTextAtSize(texto, tamano);
  return espaciado !== 0 && texto.length > 1 ? base + espaciado * (texto.length - 1) : base;
}

/**
 * Achica el texto hasta que entre en `ancho`; si ni en el mínimo entra, lo corta con
 * "…".
 *
 * Existe por los casos reales que el diseño no contempla: un nombre completo con dos
 * apellidos, o un título de curso largo. Sin esto, el texto se sale de la caja y pisa
 * la cenefa — un certificado roto es peor que uno con la tipografía dos puntos más
 * chica.
 *
 * El espaciado se achica en proporción al cuerpo: es un `letter-spacing` en px pensado
 * para ESE tamaño, y dejarlo fijo mientras la letra se achica lo vuelve enorme en
 * relación al texto.
 */
function ajustarAlAncho(
  texto: string,
  fuente: PDFFont,
  capa: CapaTexto,
): { texto: string; tamano: number; espaciado: number } {
  const espaciadoBase = capa.espaciado ?? 0;
  const minimo = capa.tamanoMinimo ?? capa.tamano;
  const espaciadoDe = (t: number) => espaciadoBase * (t / capa.tamano);

  let tamano = capa.tamano;
  while (tamano > minimo && anchoDe(texto, fuente, tamano, espaciadoDe(tamano)) > capa.ancho) {
    tamano -= 0.5;
  }
  if (tamano < minimo) tamano = minimo;

  const espaciado = espaciadoDe(tamano);
  if (anchoDe(texto, fuente, tamano, espaciado) <= capa.ancho) {
    return { texto, tamano, espaciado };
  }

  let recortado = texto;
  while (
    recortado.length > 1 &&
    anchoDe(`${recortado}…`, fuente, tamano, espaciado) > capa.ancho
  ) {
    recortado = recortado.slice(0, -1);
  }
  return { texto: `${recortado.trimEnd()}…`, tamano, espaciado };
}

/** Las fuentes y el arte se cachean por URL: el mismo archivo no se baja dos veces. */
const cacheAssets = new Map<string, Promise<ArrayBuffer>>();

function bytesDe(url: string): Promise<ArrayBuffer> {
  const enCurso = cacheAssets.get(url);
  if (enCurso) return enCurso;
  const pedido = fetch(url).then((r) => {
    if (!r.ok) throw new Error(`No se pudo cargar ${url} (HTTP ${r.status})`);
    return r.arrayBuffer();
  });
  cacheAssets.set(url, pedido);
  // Un fallo no queda cacheado: el próximo intento vuelve a pedirlo.
  pedido.catch(() => cacheAssets.delete(url));
  return pedido;
}

/**
 * Genera el PDF y lo devuelve como blob, listo para descargar o para mostrar.
 *
 * Tira si no puede cargar el arte o una tipografía: un certificado a medias —sin el
 * marco, o con el nombre en otra letra— no se entrega, se avisa.
 */
export async function generarCertificado(datos: DatosCertificado): Promise<Blob> {
  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkit);

  doc.setTitle(`Certificado — ${datos.curso}`);
  doc.setAuthor('Estudio Atlas');
  doc.setSubject(`Certificado de finalización de ${datos.curso}`);
  doc.setProducer('Atlas Academy');
  doc.setCreationDate(new Date(datos.fecha));

  const { escala } = PLANTILLA;
  const anchoPt = PLANTILLA.ancho * escala;
  const altoPt = PLANTILLA.alto * escala;
  const pagina = doc.addPage([anchoPt, altoPt]);

  /* Sólo las fuentes que la plantilla usa de verdad, y `subset: true` para que sólo
     viajen los glifos que aparecen: es lo que mantiene el archivo chico aun con cuatro
     tipografías declaradas. */
  const clavesUsadas = new Set<ClaveFuente>(PLANTILLA.capas.map((c) => c.fuente));
  const fuentes = new Map<ClaveFuente, PDFFont>();

  const [artePdf] = await Promise.all([
    bytesDe(PLANTILLA.fondo),
    ...[...clavesUsadas].map(async (clave) => {
      fuentes.set(clave, await doc.embedFont(await bytesDe(FUENTES[clave]), { subset: true }));
    }),
  ]);

  /* El arte entra como página embebida y se estampa a tamaño completo. Sigue siendo
     vectorial: no hay una resolución que elegir ni un pixelado al ampliar. */
  const [arte] = await doc.embedPdf(artePdf);
  pagina.drawPage(arte, { x: 0, y: 0, width: anchoPt, height: altoPt });

  const valorDe = (campo: CampoCertificado): string => {
    if (campo === 'nombre') return datos.nombre;
    if (campo === 'curso') return datos.curso;
    return fechaDeCertificado(datos.fecha);
  };

  for (const capa of PLANTILLA.capas) {
    const crudo =
      typeof capa.contenido === 'string' ? capa.contenido : valorDe(capa.contenido.campo);
    const texto = crudo.trim();
    if (!texto) continue;

    const fuente = fuentes.get(capa.fuente);
    if (!fuente) continue;

    const { texto: final, tamano, espaciado } = ajustarAlAncho(texto, fuente, capa);
    const ancho = anchoDe(final, fuente, tamano, espaciado);
    // Centrado en la caja, que es lo que hace el `align-items:center` del artboard.
    const xPx = capa.x + (capa.ancho - ancho) / 2;

    const opciones = {
      size: tamano * escala,
      font: fuente,
      color: color(capa.color),
      opacity: capa.opacidad ?? 1,
    };
    /* La plantilla da la `y` como línea de BASE medida desde arriba (así se midió en el
       artboard); pdf-lib la quiere desde abajo. La vuelta se hace acá y sólo acá. */
    const yPt = altoPt - capa.y * escala;

    if (espaciado === 0) {
      pagina.drawText(final, { x: xPx * escala, y: yPt, ...opciones });
      continue;
    }
    /* pdf-lib no tiene `letter-spacing`, y el diseño lo usa en los dos títulos grandes.
       Se dibuja letra por letra avanzando a mano. */
    let cursor = xPx;
    for (const letra of final) {
      pagina.drawText(letra, { x: cursor * escala, y: yPt, ...opciones });
      cursor += fuente.widthOfTextAtSize(letra, tamano) + espaciado;
    }
  }

  const bytes = await doc.save();
  // `Uint8Array` → `ArrayBuffer` propio: `bytes.buffer` puede ser un buffer más grande
  // compartido, y el Blob se quedaría con basura al final.
  return new Blob([bytes.slice().buffer], { type: 'application/pdf' });
}
